import { useMutation } from '@apollo/client'
import {
  DeleteViewCustomSqlDocument,
  OptimizeSqlsWithLlmDocument,
  UpsertViewCustomSqlDocument,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import { useMe } from '@salescore/client-common'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
} from '@salescore/core'
import { Array, Option, pipe, String } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { useCallback, useMemo, useState } from 'react'

import { useViewQueryResult } from '../../../hooks/useViewQueryResult'
import { useRefetchAggregationQueryResult } from '../../../recoil/records/mutations/useRefetchAggregationQueryResult'
import { useRefetchKpiPivotQueryResult } from '../../../recoil/records/mutations/useRefetchKpiPivotQueryResult'
import { useRefetchMutation } from '../../../recoil/records/mutations/useRefetchMutation'

export interface SQLItem {
  title: string
  displaySql: string
  originalRawSql: string
  overriddenRawSql: string | undefined
  mustacheParameter: Record<string, unknown>
}

function getDisplayName(name: string, index: number): string {
  return name === '' ? `${index + 1}. SQL_${index + 1}` : `${index + 1}. ${name}`
}

export const useSqlItems = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
) => {
  const viewQueryResult = useViewQueryResult(view)
  const { aggregationQueryResults } = useRefetchAggregationQueryResult(compiledConfig, view)

  const results = useMemo(
    () => [viewQueryResult, ...aggregationQueryResults].filter(isNotNullish),
    [viewQueryResult, aggregationQueryResults],
  )
  const errors = useMemo(
    () =>
      pipe(
        results,
        Array.map((x) => x.error),
        Array.filter(isNotNullish),
        Array.filter(String.isNonEmpty),
      ),
    [results],
  )
  const sqlItems = useMemo<SQLItem[]>(
    () =>
      pipe(
        results,
        Array.flatMap((result) => result.sqlDetails ?? []),
        Array.map((item, index) => ({
          title: getDisplayName(item.name, index),
          displaySql: item.sql,
          originalRawSql: item.originalRawSql,
          overriddenRawSql: item.overriddenRawSql,
          mustacheParameter: item.mustacheParameter,
        })),
      ),
    [results],
  )

  return {
    errors,
    sqlItems,
    isEmptyQueryResults: Array.isArrayEmpty(results),
  }
}

export function useSqlSearch(sqlItems: SQLItem[]) {
  const [searchKeyword, setSearchKeyword] = useState('')
  const filteredSqlItems = useMemo(
    () => sqlItems.filter((item) => item.title.toLowerCase().includes(searchKeyword.toLowerCase())),
    [sqlItems, searchKeyword],
  )
  return { filteredSqlItems, searchSql: setSearchKeyword }
}

export const useSqlOperations = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
  query: ViewQueryList | undefined,
) => {
  const me = useMe()
  const refetchSheetMutation = useRefetchMutation(view, query)
  const refetchKpiPivotQueryResult = useRefetchKpiPivotQueryResult(compiledConfig, view)
  const refetchAggregationQueryResult = useRefetchAggregationQueryResult(compiledConfig, view)
  const [upsertViewCustomSqlMutation, { loading: isUpsertViewCustomSqlLoading }] =
    useMutation(UpsertViewCustomSqlDocument)
  const [optimizeSqlsWithLlmMutation, { loading: isOptimizeSqlsWithLlmLoading }] =
    useMutation(OptimizeSqlsWithLlmDocument)
  const [deleteViewCustomSqlMutation, { loading: isDeleteViewCustomSqlLoading }] =
    useMutation(DeleteViewCustomSqlDocument)

  const refetch = useCallback(async () => {
    switch (view.type) {
      case 'kpiPivot': {
        refetchKpiPivotQueryResult.refetch()
        break
      }
      case 'sheet': {
        await refetchSheetMutation.refetch({ kind: 'reset' })
        await refetchAggregationQueryResult.refetch()
        break
      }
      default: {
        // ダッシュボードとシート以外では呼ばれないはず
        break
      }
    }
  }, [refetchSheetMutation, refetchKpiPivotQueryResult, refetchAggregationQueryResult, view.type])

  const updateSql = useCallback(
    async ({
      originalRawSql,
      customRawSql,
      mustacheParameter,
      onCompleted,
      onError,
    }: {
      originalRawSql: string
      customRawSql: string
      mustacheParameter: Record<string, unknown>
      onCompleted: () => void
      onError: (error: Error) => void
    }) => {
      await upsertViewCustomSqlMutation({
        variables: {
          organizationId: me.organization.id,
          input: {
            viewId: view.id,
            originalRawSql,
            customRawSql,
            mustacheParameter,
          },
        },
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onCompleted: async () => {
          await refetch()
          onCompleted()
        },
        onError: () => {
          onError(new Error(t('SQLの更新に失敗しました')))
        },
      })
    },
    [me.organization.id, view.id, upsertViewCustomSqlMutation, refetch],
  )

  const optimizeSql = useCallback(
    async ({
      originalRawSql,
      mustacheParameter,
      onCompleted,
      onError,
    }: {
      originalRawSql: string
      mustacheParameter: Record<string, unknown>
      onCompleted: () => void
      onError: (error: Error) => void
    }) => {
      await optimizeSqlsWithLlmMutation({
        variables: {
          organizationId: me.organization.id,
          inputs: [{ viewId: view.id, originalRawSql, mustacheParameter }],
        },
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onCompleted: async ({ optimizeSqlsWithLlm: { failures, successes } }) => {
          if (failures.length > 0) {
            onError(new Error(t('SQLの最適化に失敗しました')))
            return
          }

          const firstSuccess = Option.getOrNull(Array.head(successes))
          if (firstSuccess !== null) {
            await refetch()
            onCompleted()
          }
        },
        onError: () => {
          onError(new Error(t('SQLの最適化に失敗しました')))
        },
      })
    },
    [me.organization.id, view.id, optimizeSqlsWithLlmMutation, refetch],
  )

  const optimizeAllSqls = useCallback(
    async ({
      sqlItems,
      onCompleted,
      onError,
    }: {
      sqlItems: SQLItem[]
      onCompleted: (message: string) => void
      onError: (error: Error) => void
    }) => {
      await optimizeSqlsWithLlmMutation({
        variables: {
          organizationId: me.organization.id,
          inputs: sqlItems.map((x) => ({
            viewId: view.id,
            originalRawSql: x.originalRawSql,
            mustacheParameter: x.mustacheParameter,
          })),
        },
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onCompleted: async ({ optimizeSqlsWithLlm: { failures, successes } }) => {
          if (failures.length > 0) {
            onError(new Error(t('{{count}}件のSQLの最適化に失敗しました', { count: failures.length })))
            // 一括最適化の場合はfailuresがあっても処理を続ける
          }

          if (successes.length > 0) {
            await refetch()
            onCompleted(t('{{count}}件のSQLを最適化しました', { count: successes.length }))
          }
        },
        onError: () => {
          onError(new Error(t('SQLの最適化に失敗しました')))
        },
      })
    },
    [me.organization.id, view.id, optimizeSqlsWithLlmMutation, refetch],
  )

  const resetSql = useCallback(
    async ({
      originalRawSql,
      onCompleted,
      onError,
    }: {
      originalRawSql: string
      onCompleted: () => void
      onError: (error: Error) => void
    }) => {
      await deleteViewCustomSqlMutation({
        variables: {
          organizationId: me.organization.id,
          input: { viewId: view.id, originalRawSql },
        },
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onCompleted: async () => {
          await refetch()
          onCompleted()
        },
        onError: () => {
          onError(new Error(t('上書きSQLのリセットに失敗しました')))
        },
      })
    },
    [me.organization.id, view.id, deleteViewCustomSqlMutation, refetch],
  )

  return {
    updateSql,
    optimizeSql,
    optimizeAllSqls,
    resetSql,
    isUpsertViewCustomSqlLoading,
    isOptimizeSqlsWithLlmLoading,
    isDeleteViewCustomSqlLoading,
  }
}
